<template>
  <div class="login-page" :class="{ 'c-dark-theme': $store.state.darkMode }">
    <!-- Toasts kept as a secondary failure notification -->
    <CToaster :autohide="3000">
      <template v-for="info in infoList">
        <CToast
          :key="info.date"
          :show="true"
          :header="info.header"
          :color="info.color"
        >
          {{ info.message }}.
        </CToast>
      </template>
    </CToaster>

    <div class="login-shell">
      <!-- LEFT: branded marketing panel (hidden on < lg) -->
      <aside class="brand-panel">
        <div class="brand-panel-content">
          <div class="brand-logo-wrap">
            <img :src="logoUrl" alt="Sinar Group" class="brand-logo" />
          </div>
          <h2 class="brand-headline">CarTradePro</h2>
          <p class="brand-tagline">
            Manage your inventory, pricing &amp; sales — all in one platform.
          </p>
          <ul class="brand-bullets">
            <li><span class="bullet-tick">✓</span> Real-time stock list &amp; filters</li>
            <li><span class="bullet-tick">✓</span> One-tap CSV export</li>
            <li><span class="bullet-tick">✓</span> Disbursement documents</li>
            <li><span class="bullet-tick">✓</span> Commission tracking</li>
          </ul>
        </div>
        <div class="brand-panel-footer">
          <small>Sinar Group © {{ year }} · {{ version }}<span v-if="buildDate"> · Built {{ buildDate }}</span></small>
        </div>
      </aside>

      <!-- RIGHT: login form -->
      <main class="form-panel">
        <div class="form-content">
          <!-- Mobile / tablet logo strip (hidden on lg+) -->
          <div class="mobile-brand d-lg-none">
            <img :src="logoUrl" alt="Sinar Group" class="mobile-logo" />
          </div>

          <h1 class="form-title">Welcome back</h1>
          <p class="form-subtitle">Sign in to manage your inventory.</p>

          <!-- Inline error banner -->
          <div
            v-if="errorMessage"
            class="error-banner"
            role="alert"
            aria-live="polite"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="error-icon"
            >
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
            <span>{{ errorMessage }}</span>
          </div>

          <form @submit.prevent="login" novalidate>
            <div class="field">
              <label for="login-email" class="field-label">Email</label>
              <div class="input-wrap">
                <span class="input-icon" aria-hidden="true">
                  <CIcon name="cil-user" />
                </span>
                <input
                  id="login-email"
                  ref="emailInput"
                  v-model="loginObj.email"
                  type="email"
                  class="input"
                  autocomplete="username email"
                  spellcheck="false"
                  autocapitalize="off"
                  placeholder="you@company.com"
                  :disabled="loading"
                />
              </div>
            </div>

            <div class="field">
              <label for="login-password" class="field-label">Password</label>
              <div class="input-wrap">
                <span class="input-icon" aria-hidden="true">
                  <CIcon name="cil-lock-locked" />
                </span>
                <input
                  id="login-password"
                  v-model="loginObj.password"
                  :type="showPassword ? 'text' : 'password'"
                  class="input input-with-suffix"
                  autocomplete="current-password"
                  placeholder="Enter your password"
                  :disabled="loading"
                  @keydown="checkCapsLock"
                  @keyup="checkCapsLock"
                />
                <button
                  type="button"
                  class="input-suffix-btn"
                  :aria-pressed="showPassword ? 'true' : 'false'"
                  :aria-label="showPassword ? 'Hide password' : 'Show password'"
                  :title="showPassword ? 'Hide password' : 'Show password'"
                  @click="togglePasswordVisibility"
                  tabindex="0"
                >
                  <svg
                    v-if="!showPassword"
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                  </svg>
                  <svg
                    v-else
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                    <line x1="1" y1="1" x2="23" y2="23"></line>
                  </svg>
                </button>
              </div>
              <small v-if="capsLockOn" class="caps-lock-hint" aria-live="polite">
                <span aria-hidden="true">⇪</span> Caps Lock is on
              </small>
            </div>

            <div class="row-between">
              <label class="remember-me">
                <input
                  type="checkbox"
                  v-model="rememberMe"
                  :disabled="loading"
                />
                <span>Remember my email</span>
              </label>
            </div>

            <button
              type="submit"
              class="submit-btn"
              :disabled="loading || !canSubmit"
            >
              <span v-if="!loading">Sign In</span>
              <span v-else class="loading-content">
                <span class="spinner"></span>
                Signing in…
              </span>
            </button>
          </form>

          <p class="form-footer-help">
            Need an account?
            <span class="contact-link">Contact your administrator</span>
          </p>

          <!-- Mobile footer with version (shown when brand panel is hidden) -->
          <div class="mobile-footer d-lg-none">
            <small>Sinar Group © {{ year }} · {{ version }}<span v-if="buildDate"> · Built {{ buildDate }}</span></small>
          </div>
        </div>
      </main>
    </div>
  </div>
</template>

<script>
export default {
  name: "Login",
  data() {
    return {
      infoList: [],
      logoUrl: "img/sinar_group.png",
      loginObj: {
        email: "",
        password: "",
      },
      showPassword: false,
      loading: false,
      errorMessage: "",
      capsLockOn: false,
      rememberMe: false,
    };
  },
  computed: {
    version() {
      // Compile-time env var written by the build script (1-build-and-push.bat)
      // before each docker build. Falls back to the runtime window.config for
      // backwards compatibility on instances built before this change.
      return (
        process.env.VUE_APP_VERSION ||
        (typeof window !== "undefined" && window.config && window.config.VERSION) ||
        ""
      );
    },
    buildDate() {
      // Stamped by vue.config.js at build time (or dev-server start).
      const raw = process.env.VUE_APP_BUILD_DATE;
      if (!raw) return "";
      const d = new Date(raw);
      if (isNaN(d.getTime())) return raw;
      // Render as e.g. "25 May 2026, 14:30"
      return d.toLocaleString("en-MY", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
    },
    year() {
      return new Date().getFullYear();
    },
    canSubmit() {
      return !!(this.loginObj.email && this.loginObj.password);
    },
  },
  mounted() {
    // Restore remembered email (no token persistence yet — that's auth-service scope)
    try {
      const remembered = localStorage.getItem("rememberedEmail");
      if (remembered) {
        this.loginObj.email = remembered;
        this.rememberMe = true;
      }
    } catch (e) {
      /* swallow — localStorage unavailable */
    }
    // Auto-focus the empty field for fastest sign-in
    this.$nextTick(() => {
      const target = this.loginObj.email ? null : this.$refs.emailInput;
      if (target && target.focus) target.focus();
    });
  },
  methods: {
    togglePasswordVisibility() {
      this.showPassword = !this.showPassword;
    },
    checkCapsLock(event) {
      // event.getModifierState is supported on all modern browsers
      if (event && typeof event.getModifierState === "function") {
        this.capsLockOn = event.getModifierState("CapsLock");
      }
    },
    login() {
      if (this.loading || !this.canSubmit) return;
      this.errorMessage = "";
      this.loading = true;
      const data = {
        email: this.loginObj.email,
        password: this.loginObj.password,
      };

      auth
        .doLogin(data)
        .then((response) => {
          // Persist email if user opted in; clear it otherwise
          try {
            if (this.rememberMe) {
              localStorage.setItem("rememberedEmail", this.loginObj.email);
            } else {
              localStorage.removeItem("rememberedEmail");
            }
          } catch (e) {
            /* swallow */
          }

          auth.recordLogin(response.result.token, response.result, false);
          this.$router.push({ path: "/dashboard" });
        })
        .catch(({ data }) => {
          const message = helper.getErrorMessage(data);
          this.errorMessage = message;
          this.toast("Sign-in failed", message, "danger");
        })
        .finally(() => {
          this.loading = false;
        });
    },
    toast(header, message, color) {
      this.infoList.push({
        date: Date.now(),
        header,
        message,
        color,
      });
    },
  },
};
</script>

<style scoped>
/* ============ Page root ============ */
.login-page {
  min-height: 100vh;
  background: #f5f6f8;
  display: flex;
  align-items: stretch;
  justify-content: stretch;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
}

.login-shell {
  display: grid;
  grid-template-columns: 1fr;
  min-height: 100vh;
  width: 100%;
}
@media (min-width: 992px) {
  .login-shell {
    grid-template-columns: 5fr 4fr;
  }
}
@media (min-width: 1400px) {
  .login-shell {
    grid-template-columns: 1fr 1fr;
  }
}

/* ============ Brand panel (left) ============ */
.brand-panel {
  display: none;
  position: relative;
  flex-direction: column;
  justify-content: space-between;
  padding: 56px 48px;
  background:
    radial-gradient(1200px 600px at 0% 0%, rgba(251, 191, 36, 0.10) 0%, transparent 60%),
    radial-gradient(1000px 800px at 100% 100%, rgba(255, 255, 255, 0.04) 0%, transparent 60%),
    linear-gradient(135deg, #1a1f2e 0%, #0f1419 100%);
  color: #ffffff;
  overflow: hidden;
}
@media (min-width: 992px) {
  .brand-panel { display: flex; }
}

/* subtle background grid texture */
.brand-panel::after {
  content: "";
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(255, 255, 255, 0.025) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.025) 1px, transparent 1px);
  background-size: 48px 48px;
  pointer-events: none;
  mask-image: radial-gradient(ellipse at center, black 0%, transparent 70%);
}

.brand-panel-content {
  position: relative;
  z-index: 1;
  max-width: 440px;
}
.brand-logo-wrap {
  margin-bottom: 40px;
}
.brand-logo {
  width: 130px;
  height: auto;
  display: block;
}
.brand-headline {
  font-size: clamp(2rem, 3vw, 2.6rem);
  font-weight: 800;
  letter-spacing: -0.02em;
  margin: 0 0 12px;
  color: #ffffff;
}
.brand-tagline {
  font-size: 1.05rem;
  line-height: 1.55;
  color: #d1d5db;
  margin: 0 0 32px;
}
.brand-bullets {
  list-style: none;
  padding: 0;
  margin: 0;
}
.brand-bullets li {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 0;
  color: #e5e7eb;
  font-size: 0.95rem;
}
.bullet-tick {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: rgba(251, 191, 36, 0.15);
  color: #fbbf24;
  font-size: 0.78rem;
  font-weight: 700;
  flex-shrink: 0;
}
.brand-panel-footer {
  position: relative;
  z-index: 1;
  color: #6b7280;
  font-size: 0.8rem;
}

/* ============ Form panel (right) ============ */
.form-panel {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 24px;
  background: #ffffff;
}
.form-content {
  width: 100%;
  max-width: 420px;
}
@media (min-width: 576px) {
  .form-panel { padding: 56px 32px; }
}
@media (min-width: 992px) {
  .form-panel { padding: 64px 48px; }
}

/* Mobile/tablet brand strip (hidden lg+) */
.mobile-brand {
  display: flex;
  justify-content: center;
  margin-bottom: 32px;
  padding-bottom: 24px;
  border-bottom: 1px solid #f1f3f5;
}
.mobile-logo {
  width: 110px;
  height: auto;
}

.form-title {
  font-size: clamp(1.6rem, 2.5vw, 2rem);
  font-weight: 800;
  color: #111827;
  letter-spacing: -0.02em;
  margin: 0 0 6px;
}
.form-subtitle {
  color: #6b7280;
  font-size: 0.95rem;
  margin: 0 0 28px;
}

/* Inline error banner */
.error-banner {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  color: #991b1b;
  font-size: 0.88rem;
  margin-bottom: 20px;
}
.error-icon {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  margin-top: 1px;
  color: #dc2626;
}

/* ============ Fields ============ */
.field {
  margin-bottom: 18px;
}
.field-label {
  display: block;
  font-size: 0.82rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 6px;
  letter-spacing: 0.01em;
}
.input-wrap {
  position: relative;
  display: flex;
  align-items: center;
}
.input-icon {
  position: absolute;
  left: 14px;
  display: flex;
  align-items: center;
  color: #9ca3af;
  pointer-events: none;
  z-index: 1;
}
.input {
  width: 100%;
  min-height: 48px;
  padding: 12px 14px 12px 44px;
  font-size: 0.95rem;
  color: #111827;
  background: #ffffff;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  outline: none;
}
.input:focus {
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
}
.input:disabled {
  background: #f9fafb;
  color: #6b7280;
  cursor: not-allowed;
}
.input::placeholder {
  color: #9ca3af;
}
.input-with-suffix {
  padding-right: 48px;
}
.input-suffix-btn {
  position: absolute;
  right: 4px;
  top: 50%;
  transform: translateY(-50%);
  width: 40px;
  height: 40px;
  background: transparent;
  border: 0;
  border-radius: 6px;
  color: #6b7280;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: color 0.15s ease, background 0.15s ease;
}
.input-suffix-btn:hover {
  color: #2563eb;
  background: #eff6ff;
}
.input-suffix-btn:focus-visible {
  outline: 2px solid #2563eb;
  outline-offset: 2px;
}

.caps-lock-hint {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
  font-size: 0.78rem;
  color: #92400e;
  background: #fffbeb;
  padding: 3px 8px;
  border-radius: 4px;
  border: 1px solid #fde68a;
}

.row-between {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 16px 0 24px;
}
.remember-me {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 0.88rem;
  color: #4b5563;
  cursor: pointer;
  user-select: none;
}
.remember-me input[type="checkbox"] {
  width: 16px;
  height: 16px;
  accent-color: #2563eb;
  cursor: pointer;
}

/* ============ Submit ============ */
.submit-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 50px;
  background: #1f2430;
  color: #ffffff;
  border: 0;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  cursor: pointer;
  transition: background 0.15s ease, transform 0.05s ease;
}
.submit-btn:hover:not(:disabled) {
  background: #2a3344;
}
.submit-btn:active:not(:disabled) {
  transform: translateY(1px);
}
.submit-btn:disabled {
  background: #9ca3af;
  cursor: not-allowed;
}
.loading-content {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}
.spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: #ffffff;
  border-radius: 50%;
  animation: spinner-spin 0.7s linear infinite;
}
@keyframes spinner-spin {
  to { transform: rotate(360deg); }
}

/* ============ Helper text / footer ============ */
.form-footer-help {
  text-align: center;
  margin-top: 24px;
  color: #6b7280;
  font-size: 0.85rem;
}
.contact-link {
  color: #2563eb;
  font-weight: 500;
}

.mobile-footer {
  text-align: center;
  margin-top: 36px;
  color: #9ca3af;
}
</style>
